/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import {
  FiPlusCircle, FiPackage, FiDollarSign, FiCalendar, FiTruck,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

const dummyProducts = [
  {
    id: 1, name: 'Mutton', category: 'Meat', unit: 'kg',
  },
  {
    id: 2, name: 'সরিষা তেল', category: 'Oil', unit: 'liter',
  },
  {
    id: 3, name: 'Tomato', category: 'Vegetables', unit: 'kg',
  },
  {
    id: 4, name: 'জিরা', category: 'Spices', unit: 'kg',
  },
  {
    id: 5, name: 'Apple', category: 'Fruit', unit: 'pcs',
  },
];

const suppliers = [
  { id: 1, name: 'Fresh Meat Suppliers Ltd.' },
  { id: 2, name: 'Pure Oil Distributors' },
  { id: 3, name: 'Organic Farm Produce' },
  { id: 4, name: 'Spice Traders International' },
];

function Purchase() {
  const [form, setForm] = useState({
    productId: '',
    productName: '',
    quantity: '',
    unit: '',
    category: '',
    pricePerUnit: '',
    totalPrice: '',
    supplierId: '',
    supplierName: '',
    purchaseDate: dayjs().format('YYYY-MM-DD'),
    expiryDate: '',
    batchNumber: '',
    notes: '',
  });

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    if (form.quantity && form.pricePerUnit) {
      const total = (parseFloat(form.quantity) * parseFloat(form.pricePerUnit)).toFixed(2);
      setForm((prev) => ({ ...prev, totalPrice: total }));
    } else {
      setForm((prev) => ({ ...prev, totalPrice: '' }));
    }
  }, [form.quantity, form.pricePerUnit]);

  const handleProductSelect = (product) => {
    setForm({
      ...form,
      productId: product.id,
      productName: product.name,
      category: product.category,
      unit: product.unit,
    });
    setShowSuggestions(false);
  };

  const handleKeyDown = (e, product) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleProductSelect(product);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.productId || !form.quantity || !form.pricePerUnit || !form.supplierId) {
      toast.error('Please fill all required fields');
      return;
    }

    toast.success(
      <div>
        <p className="font-semibold">Purchase Recorded Successfully</p>
        <p>
          {form.quantity}
          {' '}
          {form.unit}
          {' '}
          of
          {' '}
          {form.productName}
        </p>
        <p>
          Total: ৳
          {form.totalPrice}
        </p>
      </div>,
      { autoClose: 3000 },
    );

    setForm({
      productId: '',
      productName: '',
      quantity: '',
      unit: '',
      category: '',
      pricePerUnit: '',
      totalPrice: '',
      supplierId: '',
      supplierName: '',
      purchaseDate: dayjs().format('YYYY-MM-DD'),
      expiryDate: '',
      batchNumber: '',
      notes: '',
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gray-50 rounded-lg shadow-sm">
      <div className="flex items-center mb-6">
        <FiPackage className="text-blue-600 text-2xl mr-2" />
        <h1 className="text-2xl font-bold text-gray-800">Purchase</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Product Information Section */}
        <section className="bg-white p-5 rounded-lg border shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center  text-slate-800">
            <FiPlusCircle className="mr-2 text-blue-500" />
            Product Information
          </h2>
          <h3 className="text-red-600 mb-4">
            Please Use only this products: Apple, জিরা, Tomato, সরিষা তেল, Mutton for demo.
            You can add any product later
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Product Search */}
            <div className="relative">
              <label htmlFor="productSearch" className="block text-sm font-medium text-gray-700 mb-1">
                Product *
              </label>
              <input
                id="productSearch"
                type="text"
                placeholder="Search product..."
                value={form.productName}
                onChange={(e) => {
                  setForm({ ...form, productName: e.target.value });
                  if (e.target.value.length > 0) {
                    const filtered = dummyProducts.filter((p) => p.name.toLowerCase().includes(
                      e.target.value.toLowerCase(),
                    ));
                    setSuggestions(filtered);
                    setShowSuggestions(true);
                  } else {
                    setShowSuggestions(false);
                  }
                }}
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {suggestions.map((product) => (
                    <button
                      type="button"
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      onKeyDown={(e) => handleKeyDown(e, product)}
                      className="w-full text-left px-3 py-2 hover:bg-blue-50 cursor-pointer focus:outline-none focus:bg-blue-50"
                    >
                      <div className="font-medium">{product.name}</div>
                      <div className="text-sm text-gray-600">
                        {product.category}
                        {' '}
                        (
                        {product.unit}
                        )
                      </div>
                    </button>
                  ))}
                </ul>
              )}
            </div>

            {/* Category (auto-filled) */}
            <div>
              <label htmlFor="productCategory" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <input
                id="productCategory"
                type="text"
                value={form.category}
                readOnly
                className="w-full bg-gray-100 border border-gray-300 p-2 rounded-md"
              />
            </div>

            {/* Quantity and Unit */}
            <div>
              <label htmlFor="productQuantity" className="block text-sm font-medium text-gray-700 mb-1">
                Quantity *
              </label>
              <div className="flex">
                <input
                  id="productQuantity"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="flex-1 border border-gray-300 p-2 rounded-l-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  id="productUnit"
                  type="text"
                  value={form.unit}
                  readOnly
                  className="w-20 bg-gray-100 border border-gray-300 p-2 rounded-r-md text-center"
                />
              </div>
            </div>

            {/* Price */}
            <div>
              <label htmlFor="productPrice" className="block text-sm font-medium text-gray-700 mb-1">
                Price Per Unit (৳) *
              </label>
              <div className="relative">
                <input
                  id="productPrice"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={form.pricePerUnit}
                  onChange={(e) => setForm({ ...form, pricePerUnit: e.target.value })}
                  className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-8"
                />
                <FiDollarSign className="absolute left-2 top-3 text-gray-400" />
              </div>
            </div>

            {/* Total Price (auto-calculated) */}
            <div>
              <label htmlFor="totalPrice" className="block text-sm font-medium text-gray-700 mb-1">
                Total Price
              </label>
              <div className="relative">
                <input
                  id="totalPrice"
                  type="text"
                  value={form.totalPrice ? `৳${form.totalPrice}` : ''}
                  readOnly
                  className="w-full bg-gray-100 border border-gray-300 p-2 rounded-md pl-8 font-medium"
                />
                <FiDollarSign className="absolute left-2 top-3 text-gray-600" />
              </div>
            </div>
          </div>
        </section>

        {/* Supplier and Date Information */}
        <section className="bg-white p-5 rounded-lg border shadow-sm">
          <h2 className="text-lg font-semibold mb-4 flex items-center text-slate-800">
            <FiTruck className="mr-2 text-blue-500" />
            Supplier & Date Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Supplier Selection */}
            <div>
              <label htmlFor="supplierSelect" className="block text-sm font-medium text-gray-700 mb-1">
                Supplier *
              </label>
              <select
                id="supplierSelect"
                value={form.supplierId}
                onChange={(e) => {
                  const selectedSupplier = suppliers.find(
                    (s) => s.id === parseInt(e.target.value, 10),
                  );
                  setForm({
                    ...form,
                    supplierId: e.target.value,
                    supplierName: selectedSupplier ? selectedSupplier.name : '',
                  });
                }}
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select Supplier</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Purchase Date */}
            <div>
              <label htmlFor="purchaseDate" className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Date
              </label>
              <div className="relative">
                <input
                  id="purchaseDate"
                  type="date"
                  value={form.purchaseDate}
                  onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })}
                  className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <FiCalendar className="absolute right-3 top-3 text-gray-400" />
              </div>
            </div>

            {/* Expiry Date */}
            <div>
              <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date
              </label>
              <div className="relative">
                <input
                  id="expiryDate"
                  type="date"
                  value={form.expiryDate}
                  onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
                  className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <FiCalendar className="absolute right-3 top-3 text-gray-400" />
              </div>
            </div>

            {/* Batch Number */}
            <div>
              <label htmlFor="batchNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Batch/Lot Number
              </label>
              <input
                id="batchNumber"
                type="text"
                placeholder="Optional"
                value={form.batchNumber}
                onChange={(e) => setForm({ ...form, batchNumber: e.target.value })}
                className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </section>

        {/* Additional Notes */}
        <section className="bg-white p-5 rounded-lg border shadow-sm">
          <h2 className="text-lg font-semibold mb-4  text-slate-800">Additional Information</h2>
          <label htmlFor="purchaseNotes" className="sr-only">
            Notes about this purchase
          </label>
          <textarea
            id="purchaseNotes"
            placeholder="Notes about this purchase (quality, special instructions, etc.)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            className="w-full border border-gray-300 p-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </section>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-6 py-3 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 font-medium flex items-center"
          >
            <FiPlusCircle className="mr-2" />
            Record Purchase
          </button>
        </div>
      </form>
    </div>
  );
}

export default Purchase;
